import React, {
  useEffect, useCallback, useState, useMemo, MutableRefObject, useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@material-ui/core';

import PromptText from './PromptText';
import { PrompterConfig } from '../utils';

import {
  setScrollIntervalId,
  setPlayPrompter,
  setScrollSpeed,
  setScrollPosition,
  setCameraRecording,
  setSetupAndTogglePrompter, toggleCameraRecording,
} from '../../../store/modules/studio/actions';
import {
  selectIsAutoScrolling,
  selectFontSize,
  selectScrollIntervalId,
  selectIsPlayPrompter,
  selectIsSelectDropdownOpen,
  selectScrollSpeed,
  selectBookmarkPosition, selectTeleprompterSync, selectStudioMode, selectIsCountdown,
} from '../../../store/modules/studio/selectors';
import theme from '../../../theme';
import { UploadRecordContext } from '../record/UploadRecordContext';
import {StudioMode} from "../../../configs/studio";

type Props = {
  refPrompter: MutableRefObject<HTMLDivElement | null>;
};

const PrompterComponent = ({ refPrompter }: Props) => {
  const dispatch = useDispatch();
  const [currentSpeedScalar, setCurrentSpeedScalar] = useState(1);

  const isPlayPrompter = useSelector(selectIsPlayPrompter);
  const isAutoScrolling = useSelector(selectIsAutoScrolling);
  const currentFontSize = useSelector(selectFontSize);
  const scrollIntervalId = useSelector(selectScrollIntervalId);
  const isOpenDropdown = useSelector(selectIsSelectDropdownOpen);
  const scrollSpeed = useSelector(selectScrollSpeed);
  const bookmarkPosition = useSelector(selectBookmarkPosition);
  const isTeleprompterSync = useSelector(selectTeleprompterSync);
  const studioMode = useSelector(selectStudioMode);
  const isCountdown = useSelector( selectIsCountdown);
  const { setCurrentStreamId, streamData, setOpenPreviewModal } = useContext(UploadRecordContext);

  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'));

  const getSpeedScalar = useMemo((): number => {
    if (isMobile) return PrompterConfig.speedScalarMobile;
    if (isTablet) return PrompterConfig.speedScalarTablet;
    return 1.0;
  }, [isMobile, isTablet]);

  const requestPrompterPosition = useCallback((e: React.MouseEvent | KeyboardEvent | null, top: number) => {
    if (e) {
      e.preventDefault();
    }
    if (isAutoScrolling) {
      dispatch(setScrollPosition(() => refPrompter.current?.scrollTo({ top })));
    } else {
      refPrompter.current?.scrollTo({ top });
    }
  }, [dispatch, isAutoScrolling, refPrompter]);

  useEffect(() => {
    window.requestPrompterPosition = requestPrompterPosition;
    return () => {
      window.requestPrompterPosition = undefined;
    };
  }, [requestPrompterPosition]);

  const setPrompterScrollingHandler = useCallback((fontSize: number, deviceScalar: number = 1.0) => {
    const prompter = refPrompter.current;
    if (prompter !== null) {
      let top = prompter.scrollTop;
      const intervalId = window.setInterval(
        async () => {
          prompter.scrollTo({ top });
          /*
             Step size is a function of:
                - scrollSpeed: the current scroll speed setting
                - PrompterConfig.scrollScalar: a constant scaling factor in the prompter config
                - fontSizeScalar: the ratio of the current font size vs the minimum font size
                - deviceScalar: A device scalar (mobile, tablet, desktop)
           */
          const fontSizeScalar = fontSize / PrompterConfig.minFontSize;
          const stepSize = scrollSpeed * PrompterConfig.scrollScalar * fontSizeScalar * deviceScalar;
          top += stepSize;
          if (prompter.scrollTop >= prompter.scrollHeight - prompter.offsetHeight) {
            dispatch(setPlayPrompter(false));
            window.clearInterval(intervalId);
            dispatch(setScrollIntervalId(null));
            // 2020.11.13,bn: disabled "recording stop" until we can decide whether to parameterize the option
            if (isTeleprompterSync && PrompterConfig.cameraStopRecordingWhenPrompterEnds ) {
              dispatch(setCameraRecording(false));
              const timer = setTimeout(() => {
                if (streamData.length) {
                  setCurrentStreamId(streamData[streamData.length - 1].id);
                  setOpenPreviewModal(true);
                  clearTimeout(timer);
                }
              }, 0);
            }
            // Force to bookmark or top
            top = bookmarkPosition || 0;
            prompter.scrollTo({ top });
          }
        },
        PrompterConfig.scrollIntervalMs,
      );
      dispatch(setScrollIntervalId(intervalId));
    }
  }, [dispatch, refPrompter, scrollSpeed, bookmarkPosition, isTeleprompterSync]);

  useEffect(() => {
    if (isPlayPrompter) {
      dispatch(setSetupAndTogglePrompter());
    }
    // eslint-disable-next-line
  }, []);

  const setPrompterSpeedSlower = useCallback(() => {
    const speed = Math.max(scrollSpeed - PrompterConfig.speedDelta, 0);
    dispatch(setScrollSpeed(speed));
  }, [scrollSpeed, dispatch]);

  const setPrompterSpeedFaster = useCallback(() => {
    const speed = Math.min(scrollSpeed + PrompterConfig.speedDelta, PrompterConfig.maxSpeed);
    dispatch(setScrollSpeed(speed));
  }, [scrollSpeed, dispatch]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const element = refPrompter.current;
    if (element !== null) {
      const top = element.scrollTop;
      const height = element.offsetHeight;

      switch (e.key) {
        case 'Home':
          requestPrompterPosition(e, 0);
          break;
        case 'ArrowUp':
          if (!isOpenDropdown) {
            requestPrompterPosition(e, top - currentFontSize * PrompterConfig.highlightGuide);
          }
          break;
        case 'ArrowDown':
          if (!isOpenDropdown) {
            requestPrompterPosition(e, top + currentFontSize * PrompterConfig.highlightGuide);
          }
          break;
        case 'PageUp':
          requestPrompterPosition(e, top - height);
          break;
        case 'PageDown':
          requestPrompterPosition(e, top + height);
          break;
        case 'ArrowRight':
          setPrompterSpeedFaster();
          break;
        case 'ArrowLeft':
          setPrompterSpeedSlower();
          break;
        case ' ':
          if (isTeleprompterSync && studioMode === StudioMode.CAMERA) {
            dispatch(toggleCameraRecording());
          }
          e.preventDefault();
          dispatch(setSetupAndTogglePrompter());
          break;

        default:
          break;
      }
    }
  }, [
    dispatch,
    currentFontSize,
    setPrompterSpeedFaster,
    setPrompterSpeedSlower,
    requestPrompterPosition,
    refPrompter,
    isOpenDropdown,
    studioMode,
    isTeleprompterSync,
  ]);

  useEffect(() => {
    const keyDownHandler = handleKeyDown;

    window.addEventListener('keydown', keyDownHandler);
    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, [handleKeyDown, isOpenDropdown]);

  useEffect(() => {
    if (isAutoScrolling && ( scrollIntervalId === null) && !isCountdown ) {
      setPrompterScrollingHandler(currentFontSize, getSpeedScalar);
    } else if (!isAutoScrolling && scrollIntervalId !== null) {
      window.clearInterval(scrollIntervalId);
      dispatch(setScrollIntervalId(null));
    }
  }, [
    dispatch,
    isAutoScrolling,
    setPrompterScrollingHandler,
    refPrompter,
    scrollIntervalId,
    getSpeedScalar,
    currentFontSize,
    isCountdown
  ]);

  // update scroll speed by device screen
  useEffect(() => {
    if (getSpeedScalar !== currentSpeedScalar) {
      setCurrentSpeedScalar(getSpeedScalar);
      if (isAutoScrolling && scrollIntervalId !== null) {
        window.clearInterval(scrollIntervalId);
        dispatch(setScrollIntervalId(null));
        setPrompterScrollingHandler(currentFontSize, getSpeedScalar);
      }
    }
  },
  [
    setPrompterScrollingHandler,
    currentSpeedScalar,
    setCurrentSpeedScalar,
    getSpeedScalar,
    isAutoScrolling,
    scrollIntervalId,
    currentFontSize,
  ]);

  // update scroll speed on font size change
  /* eslint react-hooks/exhaustive-deps: 0 */
  useEffect(() => {
    if (scrollIntervalId) {
      window.clearInterval(scrollIntervalId);
      dispatch(setScrollIntervalId(null));
      setPrompterScrollingHandler(currentFontSize, getSpeedScalar);
    }
  }, [currentFontSize]);

  return (
    <PromptText
      refPrompter={refPrompter}
      requestPrompterPosition={requestPrompterPosition}
    />
  );
};


export default PrompterComponent;
